import {
    Avatar,
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableSortLabel,
    TextField,
    Popper,
    List,
    ListItem,
    ListItemText, Tooltip, IconButton,
} from '@mui/material';
import React, {useState, useEffect, useMemo, useRef} from 'react';
import { PriceInputCell } from "./price-input-cell";
import AddCircleIcon from '@mui/icons-material/AddCircle';

const headCells = [
    { id: 'name', label: 'Channel' },
    { id: 'country', label: 'Country' },
    { id: 'youtubeintegration', label: 'YouTube Integration' },
    { id: 'youtubededicated', label: 'YouTube Dedicated Video' },
    { id: 'youtubeshorts', label: 'YouTube Shorts' },
    { id: 'stream2h', label: '2H Stream (TW/YT/Kick)' },
    { id: 'instagramstory', label: 'Instagram Story' },
    { id: 'instagramreel', label: 'Instagram Reel' },
    { id: 'tiktok', label: 'TikTok' },
    { id: 'xpost', label: 'X' },
];

const priceKeys = headCells.map((h) => h.id).filter((id) => !['name', 'country'].includes(id));

const getPlatformNameFromKey = (key) => {
    if (key.startsWith('youtube')) return 'YouTube';
    if (key.startsWith('instagram')) return 'Instagram';
    if (key === 'stream2h') return 'Twitch';
    if (key === 'tiktok') return 'TikTok';
    return 'X';
};

export const TalentTable = ({ data: rawData, contact, savePrice, suggestChannels, agency }) => {
    const [data, setData] = useState(null);
    const [orderBy, setOrderBy] = useState(localStorage.getItem('sortColumn') || 'name');
    const [order, setOrder] = useState(localStorage.getItem('sortOrder') || 'asc');
    const [newRow, setNewRow] = useState({ name: '', country: '', prices: {} });
    const [suggestions, setSuggestions] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);

    const debounceTimer = useRef(null);

    const getPlatformKey = (platformName, format) => {
        const normFormat = (format || '').trim().toLowerCase().replace(/\s/g, '');
        if (platformName === 'YouTube') {
            return normFormat === 'youtubeshorts' ? 'youtubeshorts' : normFormat || 'youtubeintegration';
        }
        if (platformName === 'Instagram') return normFormat || 'instagramstory';
        if (platformName === 'Twitch') return 'stream2h';
        if (platformName === 'TikTok') return 'tiktok';
        return 'xpost';
    };

    useEffect(() => {
        if (!rawData || !rawData.length) return;

        const items = rawData.map((c) => {
            const prices = {};
            const platforms = c.addChannelInfo?.platforms || [];

            for (const platform of platforms) {
                const key = getPlatformKey(platform.name, platform.format);
                prices[key] = platform.price || 0;
            }

            return {
                id: c.addChannelInfo._id,
                name: c.channelName,
                country: c.countryCode,
                updated: c.addChannelInfo?.updatedAt,
                addChannelInfo: c.addChannelInfo,
                prices,
            };
        });

        setData(items);
    }, [rawData]);

    useEffect(() => {
        const hasUnsavedData = () => {
            return newRow.name || newRow.country || Object.keys(newRow.prices || {}).length > 0;
        };

        const handleMouseLeave = (e) => {
            if (e.clientY <= 0 && hasUnsavedData()) {
                // nur wenn Maus oben rausgeht (Tab verlassen)
                alert("You have unsaved changes in the new channel row.");
            }
        };

        const handleBeforeUnload = (e) => {
            if (hasUnsavedData()) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        document.addEventListener('mouseleave', handleMouseLeave);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            document.removeEventListener('mouseleave', handleMouseLeave);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [newRow]);

    const handleChannelInputChange = (e) => {
        const val = e.target.value;
        setNewRow((prev) => ({ ...prev, name: val }));
        setAnchorEl(e.currentTarget);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        if (val.length < 2) {
            setSuggestions([]);
            return;
        }

        debounceTimer.current = setTimeout(async () => {
            try {
                const result = await suggestChannels({ query: val });
                setSuggestions(result?.data?.suggestions || []);
            } catch (err) {
                console.error('Suggest API failed', err);
            }
        }, 400);
    };

    const handleSuggestionSelect = (suggestion) => {
        setNewRow({
            name: suggestion.name,
            country: suggestion.countryCode,
            prices: {},
            id: suggestion.id || null,
            addChannelInfo: suggestion.addChannelInfo,
        });
        setSuggestions([]);
        setAnchorEl(null);
    };

    const handleSaveNewRow = () => {
        if (!newRow.name || !newRow.country) return;

        const existingACI = typeof newRow.addChannelInfo === 'string'
            ? { _id: newRow.addChannelInfo }
            : (newRow.addChannelInfo || {});

        const fullRow = {
            ...newRow,
            addChannelInfo: {
                ...existingACI,
                agency: agency,
                platforms: Object.entries(newRow.prices).map(([key, value]) => ({
                    name: getPlatformNameFromKey(key),
                    format: key,
                    price: value,
                })),
            },
        };

        savePrice({
            id: fullRow.id,
            addChannelInfo: fullRow.addChannelInfo,
        });

        setData((prev) => [...prev, { ...fullRow, id: fullRow.id || Math.random().toString() }]);
        setNewRow({ name: '', country: '', prices: {}, addChannelInfo: {} });
    };


    const handlePriceChange = (changedRow, key, newValue) => {
        setData((prev) => {
            const index = prev.findIndex((row) => row.id === changedRow.id);
            if (index === -1) return prev;

            const prevRow = prev[index];
            const prevPlatforms = prevRow.addChannelInfo?.platforms || [];
            const platformName = getPlatformNameFromKey(key);
            const format = key;

            const existingIndex = prevPlatforms.findIndex(
                (p) => p.name === platformName && (p.format ?? '') === format
            );

            const updatedPlatforms = [...prevPlatforms];
            if (existingIndex !== -1) {
                updatedPlatforms[existingIndex] = {
                    ...updatedPlatforms[existingIndex],
                    price: newValue,
                };
            } else {
                updatedPlatforms.push({ name: platformName, format, price: newValue });
            }

            const updatedRow = {
                ...prevRow,
                prices: {
                    ...prevRow.prices,
                    [key]: newValue,
                },
                addChannelInfo: {
                    ...prevRow.addChannelInfo,
                    platforms: updatedPlatforms,
                },
            };

            savePrice({ id: updatedRow.id, addChannelInfo: updatedRow.addChannelInfo });

            const updated = [...prev];
            updated[index] = updatedRow;
            return updated;
        });
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);
        localStorage.setItem('sortColumn', property);
        localStorage.setItem('sortOrder', newOrder);
    };

    const sortedData = useMemo(() => {
        if (!data) return [];

        return [...data].sort((a, b) => {
            const getValue = (item) => {
                if (orderBy === 'name' || orderBy === 'country') return item[orderBy];
                return item.prices[orderBy] || 0;
            };

            const valA = getValue(a);
            const valB = getValue(b);
            if (valA < valB) return order === 'asc' ? -1 : 1;
            if (valA > valB) return order === 'asc' ? 1 : -1;
            return 0;
        });
    }, [data, order, orderBy]);

    return (
        <Box sx={{ p: 4 }}>
            {data && (
                <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: '70vh', overflowX: 'auto' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {headCells.map((headCell) => (
                                    <TableCell key={headCell.id}>
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(headCell.id)}
                                        >
                                            {headCell.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedData.map((row, rowIdx) => (
                                <TableRow key={row.id}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Avatar>{row.name?.[0]?.toUpperCase() || 'T'}</Avatar>
                                            <Box>
                                                <Typography fontWeight="bold">{row.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Last update: {row.addChannelInfo?.updatedAt
                                                    ? new Date(row.addChannelInfo.updatedAt).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                    })
                                                    : '—'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{row.country}</TableCell>
                                    {priceKeys.map((key) => (
                                        <TableCell key={key}>
                                            <PriceInputCell
                                                value={row.prices[key]}
                                                onChange={(val) => handlePriceChange(row, key, val)}
                                                inputId={`input-${rowIdx}-${key}`}
                                                headCells={headCells}
                                                min={100}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            <TableRow>
                                {headCells.map((headCell) => {
                                    if (headCell.id === 'name') {
                                        return (
                                            <TableCell key="name">
                                                <TextField
                                                    fullWidth
                                                    variant="standard"
                                                    placeholder="New Channel Name"
                                                    value={newRow.name}
                                                    onChange={handleChannelInputChange}
                                                />
                                                <Popper open={Boolean(anchorEl && suggestions.length)} anchorEl={anchorEl} placement="bottom-start">
                                                    <Paper elevation={2}>
                                                        <List dense>
                                                            {suggestions.map((s, idx) => (
                                                                <ListItem button key={idx} onClick={() => handleSuggestionSelect(s)}>
                                                                    <ListItemText primary={`${s.name} (${s.countryCode})`} />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </Paper>
                                                </Popper>
                                            </TableCell>
                                        );
                                    } else if (headCell.id === 'country') {
                                        return (
                                            <TableCell key="country">
                                                <TextField
                                                    fullWidth
                                                    variant="standard"
                                                    placeholder="Country"
                                                    value={newRow.country}
                                                    onChange={(e) =>
                                                        setNewRow((prev) => ({ ...prev, country: e.target.value }))
                                                    }
                                                />
                                            </TableCell>
                                        );
                                    } else if (headCell.id === priceKeys.at(-1)) {
                                        // letzte Preiszelle + AddButton
                                        return (
                                            <TableCell key={headCell.id} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <PriceInputCell
                                                    value={newRow.prices[headCell.id] || ''}
                                                    onChange={(val) =>
                                                        setNewRow((prev) => ({
                                                            ...prev,
                                                            prices: { ...prev.prices, [headCell.id]: val },
                                                        }))
                                                    }
                                                    inputId={`input-new-${headCell.id}`}
                                                    headCells={headCells}
                                                    min={100}
                                                />
                                                <Tooltip title="Add Channel">
                                                    <IconButton color="primary" onClick={handleSaveNewRow}>
                                                        <AddCircleIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        );
                                    } else {
                                        return (
                                            <TableCell key={headCell.id}>
                                                <PriceInputCell
                                                    value={newRow.prices[headCell.id] || ''}
                                                    onChange={(val) =>
                                                        setNewRow((prev) => ({
                                                            ...prev,
                                                            prices: { ...prev.prices, [headCell.id]: val },
                                                        }))
                                                    }
                                                    inputId={`input-new-${headCell.id}`}
                                                    headCells={headCells}
                                                    min={100}
                                                />
                                            </TableCell>
                                        );
                                    }
                                })}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {!data && (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="250px">
                    <CircularProgress size={48} thickness={4} />
                    <Typography variant="body1" color="text.secondary" mt={2}>
                        Loading Data...
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
